import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, Users, Clock, Plus, Send } from "lucide-react";
import InviteDialog from "@/components/InviteDialog";

export default function GroupPrayers() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: "", intention: "", scheduled_at: "", duration_min: 30 });
    const [inviteGroup, setInviteGroup] = useState(null);
    const [params] = useSearchParams();
    const invitedId = params.get("invite");

    const load = () => api.get("/group-prayers").then(({ data }) => setItems(data));

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!user?.id) return;
        const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), duration_min: Number(form.duration_min) };
        await api.post("/group-prayers", payload);
        setForm({ title: "", intention: "", scheduled_at: "", duration_min: 30 });
        setShowForm(false);
        load();
    };

    const join = async (id) => {
        if (!user?.id) { window.location.href = "/login"; return; }
        await api.post(`/group-prayers/${id}/join`);
        load();
    };

    const fmt = (iso) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
        } catch { return iso; }
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>Group Prayers</div>
                        <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>Coming together to pray.</h1>
                        <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>
                            Gather at a chosen hour — in spirit — for a particular intention.
                        </p>
                    </div>
                    {user?.id && (
                        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center gap-2" data-testid="group-new-button">
                            <Plus size={16} /> Schedule group prayer
                        </button>
                    )}
                </div>

                {showForm && (
                    <form onSubmit={submit} className="mt-6 p-card p-6 grid gap-4" data-testid="group-new-form">
                        <input required placeholder="Title — e.g. Rosary for my mother"
                               value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                               className="px-4 py-3 rounded-full border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="group-new-title" />
                        <textarea required placeholder="Intention to pray for" rows={3}
                                  value={form.intention} onChange={(e) => setForm({ ...form, intention: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="group-new-intention" />
                        <div className="flex gap-3 flex-wrap">
                            <input type="datetime-local" required value={form.scheduled_at}
                                   onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                                   className="px-4 py-2.5 rounded-full border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                   data-testid="group-new-time" />
                            <input type="number" min={5} max={240} value={form.duration_min}
                                   onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                                   className="px-4 py-2.5 rounded-full border w-32" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                   data-testid="group-new-duration" />
                            <span className="self-center text-sm" style={{ color: "#525252" }}>minutes</span>
                            <button type="submit" className="btn-gold ml-auto text-sm" data-testid="group-new-submit">Schedule</button>
                        </div>
                    </form>
                )}

                <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.length === 0 && (
                        <div className="col-span-full p-card p-10 text-center">
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>No group prayers yet.</div>
                            <div className="text-sm mt-1" style={{ color: "#525252" }}>Be the one to gather the brethren.</div>
                        </div>
                    )}
                    {items.map((g, i) => {
                        const highlighted = invitedId && invitedId === g.id;
                        return (
                        <div key={g.id} className={`p-card p-5 fade-up delay-${(i % 4) + 1}`}
                             style={highlighted ? { border: "2px solid #D4AF37", boxShadow: "0 14px 40px -16px rgba(212,175,55,0.4)" } : {}}
                             data-testid={`group-card-${g.id}`}>
                            {highlighted && (
                                <div className="text-[10px] tracking-[0.2em] uppercase mb-2" style={{ color: "#D4AF37" }}>✶ You were invited</div>
                            )}
                            <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase" style={{ color: "#7F1D1D" }}>
                                <Calendar size={12} /> {fmt(g.scheduled_at)}
                            </div>
                            <div className="font-display text-2xl mt-2" style={{ color: "#1E1B4B" }}>{g.title}</div>
                            <div className="text-sm mt-2" style={{ color: "#525252" }}>{g.intention}</div>
                            <div className="mt-4 flex items-center justify-between">
                                <div className="text-sm flex items-center gap-3" style={{ color: "#1E1B4B" }}>
                                    <span className="flex items-center gap-1"><Users size={14} /> {g.participants?.length || 0}</span>
                                    <span className="flex items-center gap-1"><Clock size={14} /> {g.duration_min}m</span>
                                </div>
                                <div className="flex gap-2">
                                    {user?.id && (
                                        <button onClick={() => setInviteGroup(g)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`group-invite-${g.id}`}>
                                            <Send size={12} /> {t("invite.btn")}
                                        </button>
                                    )}
                                    <button onClick={() => join(g.id)} className="btn-primary text-xs" data-testid={`group-join-${g.id}`}>Join</button>
                                </div>
                            </div>
                            <div className="mt-2 text-xs" style={{ color: "#525252" }}>Hosted by {g.host_name}</div>
                        </div>
                        );
                    })}
                </div>
            </div>
            <InviteDialog open={!!inviteGroup} onClose={() => setInviteGroup(null)} group={inviteGroup} />
        </div>
    );
}
